/**
 * Type-safe request API built on top of the original request function
 *
 * This provides enhanced type safety when working with shared API types.
 * Use this for new code, while old code can continue using the original request.
 *
 * @example
 * // Old way (still works):
 * import request from "src/api/request";
 * const data = await request("/aircraft");
 *
 * // New typed way:
 * import { typedRequest } from "src/api/typedRequest";
 * const data = await typedRequest.get<AircraftTypesResponse>("/aircraft");
 *
 * // With params and mapper:
 * const data = await typedRequest.get({
 *   url: "/aircraft/crew/:crewId",
 *   params: { crewId: 123 },
 *   mapper: (response: AircraftTypesResponse) => response.map(item => new AircraftType(item))
 * });
 */

import request from "./request";
import { buildUrl } from "../utils/api";

interface RequestConfig<TParams, TResponse, TMapped> {
  url: string;
  params?: TParams;
  body?: object | FormData;
  mapper?: (response: TResponse) => TMapped;
}

/**
 * Generic typed request function
 */
async function typedRequest<T = any>(url: string, method: string = "GET", body?: object | FormData): Promise<T> {
  return request(url, method, body) as Promise<T>;
}

/**
 * GET request with optional params and mapper
 */
typedRequest.get = async <TParams = any, TResponse = any, TMapped = TResponse>(
  config: string | RequestConfig<TParams, TResponse, TMapped>,
): Promise<TMapped> => {
  if (typeof config === "string") {
    return typedRequest<TResponse>(config) as any;
  }
  const { url, params, mapper } = config;
  const finalUrl = params ? buildUrl(url, params) : url;
  const response = await typedRequest<TResponse>(finalUrl);
  return (mapper ? mapper(response) : response) as TMapped;
};

/**
 * POST request with optional params, body, and mapper
 * Supports both old style: post(url, body) and new style: post({ url, params, body, mapper })
 */
typedRequest.post = async <TParams = any, TResponse = any, TMapped = TResponse>(
  configOrUrl: string | RequestConfig<TParams, TResponse, TMapped>,
  body?: object | FormData,
): Promise<TMapped> => {
  if (typeof configOrUrl === "string") {
    return typedRequest<TResponse>(configOrUrl, "POST", body) as any;
  }
  const { url, params, body: configBody, mapper } = configOrUrl;
  const finalUrl = params ? buildUrl(url, params) : url;
  const response = await typedRequest<TResponse>(finalUrl, "POST", configBody);
  return (mapper ? mapper(response) : response) as TMapped;
};

/**
 * PUT request with optional params, body, and mapper
 * Supports both old style: put(url, body) and new style: put({ url, params, body, mapper })
 */
typedRequest.put = async <TParams = any, TResponse = any, TMapped = TResponse>(
  configOrUrl: string | RequestConfig<TParams, TResponse, TMapped>,
  body?: object | FormData,
): Promise<TMapped> => {
  if (typeof configOrUrl === "string") {
    return typedRequest<TResponse>(configOrUrl, "PUT", body) as any;
  }
  const { url, params, body: configBody, mapper } = configOrUrl;
  const finalUrl = params ? buildUrl(url, params) : url;
  const response = await typedRequest<TResponse>(finalUrl, "PUT", configBody);
  return (mapper ? mapper(response) : response) as TMapped;
};

/**
 * DELETE request with optional params and mapper
 */
typedRequest.delete = async <TParams = any, TResponse = any, TMapped = TResponse>(
  config: string | RequestConfig<TParams, TResponse, TMapped>,
): Promise<TMapped> => {
  if (typeof config === "string") {
    return typedRequest<TResponse>(config, "DELETE") as any;
  }
  const { url, params, mapper } = config;
  const finalUrl = params ? buildUrl(url, params) : url;
  const response = await typedRequest<TResponse>(finalUrl, "DELETE");
  return (mapper ? mapper(response) : response) as TMapped;
};

/**
 * PATCH request with optional params, body, and mapper
 */
typedRequest.patch = async <TParams = any, TResponse = any, TMapped = TResponse>(
  configOrUrl: string | RequestConfig<TParams, TResponse, TMapped>,
  body?: object | FormData,
): Promise<TMapped> => {
  if (typeof configOrUrl === "string") {
    return typedRequest<TResponse>(configOrUrl, "PATCH", body) as any;
  }
  const { url, params, body: configBody, mapper } = configOrUrl;
  const finalUrl = params ? buildUrl(url, params) : url;
  const response = await typedRequest<TResponse>(finalUrl, "PATCH", configBody);
  return (mapper ? mapper(response) : response) as TMapped;
};

export { typedRequest };
export default typedRequest;
